// aep-retention-playbook.js - Tier 1 Educational Content
// The AEP Retention Playbook: Keep Clients While Everyone Else Is Selling

export const content = {
  html: `
<p>Every year, the Annual Enrollment Period rolls around and the same thing happens: agents scramble to write new business while their existing clients quietly slip away to competitors. I've watched it happen for years, and honestly, it's one of the most frustrating patterns in this industry.</p>

<p>Here's the thing most agents don't realize—<strong>you're more likely to lose a client during AEP than any other time of year.</strong> The phones are ringing off the hook with new prospects, carriers are pushing production numbers, and meanwhile your existing book of business is being bombarded by mailers, TV ads, and other agents promising something better.</p>

<p>This playbook is about playing defense while everyone else plays offense. Because the math is simple: it costs 5-7x more to acquire a new client than to keep an existing one. And a client you lose during AEP? That's commission you'll never see again.</p>

<h2>Understanding the AEP Battlefield</h2>

<p>AEP runs from October 15th through December 7th every year. During these 54 days, Medicare beneficiaries can:</p>

<ul>
<li>Switch from one Medicare Advantage plan to another</li>
<li>Switch from Medicare Advantage back to Original Medicare</li>
<li>Join a Medicare Advantage plan from Original Medicare</li>
<li>Change Part D prescription drug plans</li>
</ul>

<p>For agents, this creates a perfect storm. Your clients have maximum flexibility to leave, competitors have maximum incentive to poach, and you have minimum time to pay attention because you're busy with new enrollments.</p>

<p>According to recent data, about 21% of Medicare Advantage enrollees report switching their coverage in the past two years. That's roughly one in five clients. And for those who do switch, the most common reasons cited are cost and the promise of better benefits elsewhere.</p>

<h2>The Pre-AEP Window: August Through Early October</h2>

<p>The real retention work happens before AEP starts. By the time October 15th hits, you're already behind if you haven't touched base with your clients.</p>

<h3>What to do 60-90 days before AEP:</h3>

<p><strong>Review your book for risk factors.</strong> Pull a list of clients who might be vulnerable:</p>

<ul>
<li>Clients who've had claims issues or complaints this year</li>
<li>Clients whose plans are dropping benefits or raising premiums</li>
<li>Clients in plans with declining Star ratings</li>
<li>Clients you haven't spoken to in 6+ months</li>
<li>Clients who enrolled last AEP (they're still in the "shopping" mindset)</li>
</ul>

<p><strong>Reach out with value, not a sales pitch.</strong> This isn't the time to push plan changes. It's the time to say, "Hey, I wanted to check in before things get crazy. How's everything going with your coverage?"</p>

<p>In my experience, this simple touchpoint catches 80% of issues before they become reasons to leave. A client who feels heard and cared for is dramatically less likely to take that call from another agent.</p>

<h3>The ANOC Moment: September</h3>

<p>Every September, carriers send out Annual Notice of Change (ANOC) letters to their members. These documents outline what's changing in the client's plan for the upcoming year—premium adjustments, benefit modifications, formulary changes, network updates.</p>

<p>Most clients either don't read these letters or don't understand them. This is your opportunity.</p>

<p><strong>What I recommend:</strong> Call your clients proactively in late September and say, "You should be receiving your ANOC letter soon. When you get it, let me know and I'll walk you through what it means for you specifically."</p>

<p>This positions you as the expert and trusted advisor. When that other agent calls during AEP, your client thinks, "I already have someone who's looking out for me."</p>

<h2>During AEP: The 54-Day Defense</h2>

<p>Once AEP starts, you're in active defense mode. Here's how to structure it:</p>

<h3>Week 1-2 (October 15-31): Prioritize High-Risk Clients</h3>

<p>Focus your retention calls on the clients you identified as high-risk. Don't wait for them to call you with questions—by then, they may have already started shopping.</p>

<p>The script is simple: "AEP just started, and I wanted to make sure you're feeling good about your coverage for next year. Based on your plan's changes, here's what I think you should consider..."</p>

<p>Even if nothing needs to change, the client feels attended to. That matters.</p>

<h3>Week 3-4 (November 1-14): Cast a Wider Net</h3>

<p>Now reach out to the rest of your book—the clients who seemed stable. A quick call, email, or text: "Just checking in during enrollment season. Any questions about your plan for next year?"</p>

<p>You'd be surprised how often "stable" clients have been quietly considering a switch but hadn't bothered to tell you.</p>

<h3>Week 5-8 (November 15 - December 7): Watch for Late Shoppers</h3>

<p>The final weeks of AEP see a surge in activity. Clients who procrastinated are now rushing to make decisions, often poorly informed ones.</p>

<p>This is when you might get calls from clients who've already talked to another agent. Don't panic—and don't get defensive. Listen to what they were offered, then calmly explain the full picture.</p>

<p>Often, competitors promise benefits without explaining the tradeoffs. Your job is to be the voice of reason.</p>

<h2>What Actually Makes Clients Leave During AEP</h2>

<p>After years in this business, I can tell you the top reasons clients switch agents during AEP—and most of them are preventable:</p>

<h3>1. They Don't Remember You</h3>

<p>If you only contact clients once a year during enrollment, you're not their agent—you're a stranger who calls annually. Clients switch to someone who "seems more attentive" because attentive is a low bar when you've been silent for 12 months.</p>

<h3>2. Their Situation Changed and You Didn't Know</h3>

<p>New diagnosis. New prescriptions. Moved to a new area. Changed doctors. Any of these can make a client's current plan suboptimal—and if you don't know about it, you can't help them. They'll find out from someone else, and that someone else gets the business.</p>

<h3>3. They Got Spooked by Plan Changes</h3>

<p>Premium went up $10. A pharmacy left the network. Their favorite benefit got modified. Small changes feel big when no one explains them. Without context from you, clients assume the worst and start shopping.</p>

<h3>4. Someone Promised Something Better</h3>

<p>Competitors make aggressive pitches during AEP. "$0 premium! Dental AND vision! Grocery benefits!" What they don't mention is the narrower network, higher out-of-pocket maximums, or formulary restrictions.</p>

<p>Clients who trust you will ask before switching. Clients who don't know you will just switch.</p>

<h2>Retention Tactics That Actually Work</h2>

<h3>The Birthday Call</h3>

<p>This has nothing to do with AEP timing, but everything to do with AEP retention. Call your clients on their birthday. Not a text, not an automated email—an actual phone call. Two minutes of "Happy birthday, how's everything going?" builds more loyalty than any marketing campaign.</p>

<h3>The Quarterly Check-In</h3>

<p>Four times a year, reach out to your book. Doesn't need to be long. "Hey, just checking in. Anything changed with your health or coverage needs?" This surfaces issues early and keeps you top of mind.</p>

<h3>The Service Recovery</h3>

<p>When something goes wrong—a claim denied, a prior auth delayed, a pharmacy mix-up—be the one who fixes it. Clients don't expect perfection. They expect someone to help when things aren't perfect. Be that person, and they'll never leave.</p>

<h3>The Educational Touch</h3>

<p>Send occasional updates about Medicare news that affects your clients. New benefits coming, rule changes, things to watch for. This positions you as an ongoing resource, not just someone who shows up during enrollment.</p>

<h2>The Numbers Game</h2>

<p>Let's be real about the math:</p>

<ul>
<li>The average Medicare agent loses 15-25% of their book annually to churn</li>
<li>Replacing those clients costs significantly more than retaining them</li>
<li>A client retained for 5+ years is worth 3-5x their first-year commission in lifetime value</li>
</ul>

<p>If you have 200 clients and you're losing 20% per year, that's 40 clients gone. At an average of $300 in annual commission per client, you're losing $12,000 before you've written a single new enrollment.</p>

<p>Cut that churn rate in half through active retention work, and you've effectively "earned" $6,000 without writing new business. That's the retention ROI that most agents ignore.</p>

<h2>Technology and Systems</h2>

<p>You can't manage retention with sticky notes and memory. You need systems:</p>

<ul>
<li><strong>A CRM that tracks every client interaction</strong> – When did you last call? What did you discuss? What's their plan status?</li>
<li><strong>Automated reminders</strong> – Birthdays, policy anniversaries, ANOC season, check-in schedules</li>
<li><strong>Risk identification</strong> – Which clients have red flags? Who needs attention first?</li>
<li><strong>Communication templates</strong> – Pre-written but personalizable outreach for each touchpoint</li>
</ul>

<p>The agents who retain best aren't necessarily working harder—they're working more systematically.</p>

<h2>The Mindset Shift</h2>

<p>Here's what it comes down to: most agents treat AEP as sales season. The best agents treat it as retention season with some sales mixed in.</p>

<p>Your existing clients are your most valuable asset. They're already sold on you—you just need to remind them why. The new prospects? They're expensive to acquire and more likely to churn. Your loyal book is where the real money is.</p>

<p>So this AEP, before you chase that next enrollment, ask yourself: have I taken care of the clients who already trust me?</p>
  `,

  faqs: [
    {
      question: "When should I start preparing for AEP retention?",
      answer: "Start 60-90 days before AEP (August-September). This gives you time to review your book for at-risk clients, make proactive touchpoints, and position yourself as a resource before the chaos starts. By October 15th, you should have already contacted your high-risk clients."
    },
    {
      question: "What's the biggest mistake agents make during AEP?",
      answer: "Focusing exclusively on new sales while ignoring existing clients. Your book is being actively marketed to by competitors during AEP. If you're not talking to your clients, someone else is. The agents who retain best treat AEP as defense season, not just offense."
    },
    {
      question: "How do I identify which clients are at risk of leaving?",
      answer: "Look for: clients whose plans have significant changes (premiums, benefits, networks), clients you haven't contacted in 6+ months, clients who enrolled last year (still in shopping mode), clients who've had service issues, and clients in plans with declining Star ratings. These need proactive outreach first."
    },
    {
      question: "What should I say when a client tells me they're considering switching?",
      answer: "Don't get defensive. Listen to what they were offered, then calmly walk through the full picture—including what the competitor might not have mentioned (narrower networks, higher out-of-pocket costs, formulary restrictions). Be the voice of reason, not the desperate salesperson."
    },
    {
      question: "How much time should I spend on retention vs. new sales during AEP?",
      answer: "It depends on your book size, but a good rule is 40-50% of your AEP time on retention activities. The math supports it: it costs 5-7x more to acquire a new client than keep an existing one. A retained client has higher lifetime value than a new enrollment."
    },
    {
      question: "Can I contact clients about their plans before AEP starts?",
      answer: "Yes, with compliance considerations. Pre-AEP is the perfect time for service calls—checking in on satisfaction, reviewing upcoming plan changes, answering questions. You're not selling during these calls; you're serving. Just ensure all communications comply with CMS marketing guidelines."
    }
  ],

  sources: [
    {
      title: "Medicare Advantage in 2024: Enrollment Update and Key Trends",
      url: "https://www.kff.org/medicare/issue-brief/medicare-advantage-in-2024-enrollment-update-and-key-trends/",
      publisher: "Kaiser Family Foundation (KFF)",
      accessedDate: "2024-12-10"
    },
    {
      title: "Open Enrollment Allows Medicare Beneficiaries to Change Coverage",
      url: "https://www.commonwealthfund.org/blog/2024/open-enrollment-allows-medicare-beneficiaries-change-coverage",
      publisher: "Commonwealth Fund",
      accessedDate: "2024-12-10"
    },
    {
      title: "Medicare Advantage and Prescription Drug Plan Disenrollment Reasons Survey",
      url: "https://www.cms.gov/research-statistics-data-and-systems/research/cahps/mapdp_disenrollmentsurvey",
      publisher: "CMS",
      accessedDate: "2024-12-10"
    },
    {
      title: "Turnover Among New Medicare Advantage Enrollees",
      url: "https://www.ajmc.com/view/turnover-among-new-medicare-advantage-enrollees-may-be-greater-than-perceived",
      publisher: "American Journal of Managed Care",
      accessedDate: "2024-12-10"
    }
  ],

  ctaDescription: `This is exactly why I started building Pro IRP. After watching agents lose good clients every AEP for preventable reasons, I wanted a system that actually helps you identify at-risk clients before they leave, automate the touchpoints that build loyalty, and stop the churn that's eating into your commissions.`
};

export default content;
